import React, { useState, useMemo } from 'react';
import { Box, Typography, Card, CardContent, Chip, alpha, useTheme } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

// Colors for different eixos
const EIXO_COLORS: Record<string, string> = {
  'c1-mais-acesso': '#1565c0', 'c2-criancas': '#2e7d32', 'c3-gestantes': '#e65100',
  'c4-diabeticos': '#6a1b9a', 'c5-hipertensos': '#00838f', 'c6-idosos': '#795548',
  'c7-mulheres': '#c62828', 'odonto': '#00695c', 'nt30-vinculo': '#455a64',
  'emulti': '#4527a0', 'producao': '#757575',
};

interface EvolucaoChartProps {
  periodos: Array<{
    label: string;
    score_geral: number;
    eixos: Array<{ eixo: string; nome: string; score_medio: number }>;
  }>;
}

const EvolucaoChart: React.FC<EvolucaoChartProps> = ({ periodos }) => {
  const theme = useTheme();

  // Get all unique eixos that appear in the data, filter to main eSF/eAP ones
  const mainEixos = ['c1-mais-acesso', 'c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres'];
  const [visibleEixos, setVisibleEixos] = useState<Set<string>>(new Set(mainEixos));

  const toggleEixo = (eixo: string) => {
    setVisibleEixos(prev => {
      const next = new Set(prev);
      if (next.has(eixo)) next.delete(eixo);
      else next.add(eixo);
      return next;
    });
  };

  // Build chart data: one entry per periodo with score_geral + per-eixo scores
  const chartData = useMemo(() => {
    return periodos.map(p => {
      const entry: any = { label: p.label, score_geral: p.score_geral };
      p.eixos.forEach(e => { entry[e.eixo] = e.score_medio; });
      return entry;
    });
  }, [periodos]);

  // Get eixo display names from first periodo that has data
  const eixoNomes = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of periodos) {
      for (const e of p.eixos) {
        if (!map[e.eixo]) map[e.eixo] = e.nome;
      }
    }
    return map;
  }, [periodos]);

  if (!periodos.length) return null;

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Evolucao Temporal</Typography>

        {/* Eixo toggles */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {mainEixos.map(eixo => (
            <Chip
              key={eixo}
              label={eixoNomes[eixo] || eixo}
              size="small"
              onClick={() => toggleEixo(eixo)}
              sx={{
                fontSize: '0.7rem', height: 24,
                bgcolor: visibleEixos.has(eixo) ? alpha(EIXO_COLORS[eixo] || '#757575', 0.15) : 'transparent',
                borderColor: EIXO_COLORS[eixo] || '#757575',
                color: visibleEixos.has(eixo) ? EIXO_COLORS[eixo] : theme.palette.text.disabled,
              }}
              variant={visibleEixos.has(eixo) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>

        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.text.primary, 0.08)} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip formatter={((value: any, name: string) => [`${value} pts`, eixoNomes[name] || name]) as any} />
            <ReferenceLine y={75} stroke="#2e7d32" strokeDasharray="3 3" strokeOpacity={0.4} />
            <ReferenceLine y={50} stroke="#1565c0" strokeDasharray="3 3" strokeOpacity={0.4} />
            <ReferenceLine y={25} stroke="#e65100" strokeDasharray="3 3" strokeOpacity={0.4} />
            {/* Score Geral line - always visible */}
            <Line type="monotone" dataKey="score_geral" stroke={theme.palette.primary.main} strokeWidth={3} dot={{ r: 4 }} name="Score Geral" />
            {/* Per-eixo lines */}
            {mainEixos.filter(e => visibleEixos.has(e)).map(eixo => (
              <Line key={eixo} type="monotone" dataKey={eixo} stroke={EIXO_COLORS[eixo]} strokeWidth={1.5} dot={{ r: 2 }} strokeDasharray="5 5" name={eixo} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default EvolucaoChart;
